import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import ActionText from '../core/ActionText';
import Errored from '../core/Errored';
import Loading from '../core/Loading';
import MenuSelector from '../core/MenuSelector';
import Paging from '../core/Paging';
import PagingFilter from '../core/PagingFilter';
import PrimaryButton from '../core/PrimaryButton';
import ProjectLanguageSelector from '../core/ProjectLanguageSelector';
import { globalStyles as styles } from '../core/style/gstheme';
import { makeCancelable, sanitiseText, sortObjects } from '../core/utils/utils';
import GS from '../grafsync/grafsync';

class ResourceStatus extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            page: 1,
            lang: props.defaultLang,
            pageSize: 20,
            text: "",
            resourceType: "All",
            searchCount: 0,
            pageCount: null,
            sortedResources: [],
            isLoading: true,
            isErrored: undefined
        };
        this.cancellable = null;
        this.cancellable1 = null;
    }

    componentDidMount() {
        this.getResources();
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.cancellable1) this.cancellable1.cancel();
    }

    getResources = async () => {
        try {
            const { page, text, lang, resourceType } = this.state;
            const typeList = resourceType === "All" ?
                this.props.resourceTypeOptions.map(o => o.value)
                :
                resourceType;
            let search = [
                {
                    predicate: this.props.predicate,
                    ...(this.props.predicateValue) && { value: this.props.predicateValue },
                    ...(this.props.predicateInverted) && { invert: true }
                },
                {
                    predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    value: typeList
                },
                {
                    predicate: this.props.within,
                    value: this.props.componentIRI
                }
            ];
            if (text !== "") {
                search.push({
                    predicate: this.props.labelType,
                    value: text,
                    isExactMatch: false
                });
            }
            GS.handle("search-resources");
            const results = await this.doSearch({
                search,
                graph: [this.props.graph],
                page,
                language: lang,
                allowMissingResults: true,
                basicSort: true,
                retainOrder: text === "",
                ...(this.props.entailment) && { entailment: ["propertyChain"] },
                pageSize: 20,
                predicates: [
                    ...this.props.labelType,
                    "http://www.w3.org/2002/07/owl#deprecated"
                ],
                sort: [...this.props.labelType]
            }).promise;
            if (this.cancellable1 && this.cancellable1.isCancelled()) return;
            const resources = results.results || null;
            const pageCount = results.pageCount;
            const searchCount = results.searchCount;
            if (resources && resources.length > 0) {
                const sortedResources = sortObjects([...resources], this.props.sortProp, lang, this.props.defaultLang, false).map(resource => resource.id);
                // Load all to avoid unwanted API calls
                try {
                    GS.handle("load-resources");
                    await this.loadData(sortedResources).promise;
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                }
                catch (e) {
                    if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                    return;
                }
                this.setState({ sortedResources, isLoading: false, page, pageCount, searchCount });
            }
            else {
                this.setState({ sortedResources: null, isLoading: false, searchCount: 0 });
            }
        }
        catch (e) {
            if (!this.cancellable1 || (this.cancellable1 && !this.cancellable1.isCancelled())) this.setState({ isErrored: e });
        }
    };

    loadData(iris, options) {
        this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
        return this.cancellable;
    }

    doSearch(payload) {
        this.cancellable1 = makeCancelable(GS.search(payload));
        return this.cancellable1;
    }

    handlePageChange = (page = 1) => {
        this.setState({ page, isLoading: true }, () => this.getResources());
    };

    handleFilterChange = (text) => {
        const cleanText = sanitiseText(text);
        if (cleanText.length === 1) return;
        this.setState({ page: 1, text: cleanText, isLoading: true }, () => this.getResources());
    };

    handleResourceTypeChange = (resourceType) => this.setState({ page: 1, resourceType, isLoading: true }, () => this.getResources());

    changeLanguage = (lang) => this.setState({ page: 1, lang, isLoading: true }, () => this.getResources());

    render() {

        const { graph, defaultLang, components, projectIRI, onToggleView, classes, resourceTypeOptions, projectType } = this.props;
        const { sortedResources, page, pageCount, isErrored, isLoading, pageSize, searchCount, lang, resourceType, text } = this.state;

        if (isErrored) return <Errored dataTest="de-error" data={isErrored} />;

        const fromResult = (page - 1) * pageSize + 1;
        const toResult = page * pageSize > searchCount ? searchCount : page * pageSize;

        return (
            <div style={{ minHeight: 260, margin: 4 }}>
                <div data-test="status-filtering" className={classes.filterArea} style={{ marginBottom: 12 }}>
                    <Grid container spacing={2} classes={
                        {
                            "spacing-xs-2": this.props.classes.fullGrid
                        }}>
                        <Grid item xs={4}>
                            <div style={{ paddingTop: 3 }}>
                                <PagingFilter
                                    disabled={isLoading}
                                    autoFocus={true}
                                    handleFilterChange={this.handleFilterChange}
                                    text={text}
                                    minLength={2}
                                    includeLabel={true}
                                    label="Label"
                                    maxLength={50} />
                            </div>

                        </Grid>
                        {resourceTypeOptions && resourceTypeOptions.length > 1 &&
                            <Grid item xs={4}>
                                <MenuSelector
                                    disabled={isLoading}
                                    dataTest="resource-type-selector"
                                    label="Resource Type"
                                    selected={resourceType}
                                    handleChange={this.handleResourceTypeChange}
                                    options={[
                                        {
                                            "value": "All",
                                            "label": "All"
                                        },
                                        ...resourceTypeOptions
                                    ]} />
                            </Grid>
                        }
                        <Grid item xs={4}>
                            <ProjectLanguageSelector
                                disabled={isLoading}
                                key={lang}
                                selectedLang={lang}
                                includeLabel={true}
                                onSelect={this.changeLanguage} />
                        </Grid>
                    </Grid>
                </div>
                {isLoading ?
                    <Loading minHeight="120px" offset="60px" message="Working on getting results ..." />
                    :
                    !sortedResources || (sortedResources && sortedResources.length === 0) ?
                        <div style={{ paddingBottom: '70px' }}>
                            <ActionText offset="80px" format="result">No results.</ActionText>
                            {page > 1 &&
                                <div style={{ textAlign: 'center', marginTop: 20 }}>
                                    <PrimaryButton dataTest="refresh" onClick={() => this.handlePageChange()}>Refresh</PrimaryButton>
                                </div>
                            }
                        </div>
                        :
                        <div>
                            <Typography
                                paragraph
                                data-test="showing-results"
                                variant="body2"
                                className={classes.showing}>Showing {fromResult} to {toResult} of {searchCount} results.</Typography>
                            <div data-test="resource-list">
                                {sortedResources.map(resource =>
                                    <CommonIriDisplay
                                        key={resource}
                                        id={resource}
                                        components={components}
                                        projectIRI={projectIRI}
                                        otherComponent={false}
                                        handleNotAvailable={true}
                                        projectType={projectType}
                                        compact={true}
                                        onLink={onToggleView}
                                        graph={graph}
                                        wrap={false}
                                        viewLang={lang}
                                        defaultLang={defaultLang} />
                                )}
                            </div>
                            {pageCount > 1 &&
                                <div style={{ marginTop: '20px' }}>
                                    <Paging page={page} pageCount={pageCount} pageItemsRange={5} handlePageChange={this.handlePageChange} />
                                </div>
                            }
                        </div>
                }
            </div>
        );
    }
}

ResourceStatus.defaultProps = {
    entailment: false,
    sortProp: "label",
    predicateValue: undefined,
    predicateInverted: false
};

ResourceStatus.propTypes = {
    id: PropTypes.string.isRequired,
    projectIRI: PropTypes.string.isRequired,
    predicate: PropTypes.string.isRequired,
    predicateValue: PropTypes.string,
    predicateInverted: PropTypes.bool,
    componentIRI: PropTypes.string.isRequired,
    resourceTypeOptions: PropTypes.array,
    within: PropTypes.array.isRequired,
    entailment: PropTypes.bool,
    labelType: PropTypes.array.isRequired,
    sortProp: PropTypes.string,
    components: PropTypes.array.isRequired,
    onToggleView: PropTypes.func.isRequired,
    defaultLang: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired
};

export default withRouter(withStyles(styles)(ResourceStatus));